<template>
  <div class="date-picker">
    <div class="header">
      <span>{{ currentYear }} 年 {{ currentMonth + 1 }} 月</span>
      <slot name="header-right" />
    </div>
    <div class="weekdays">
      <div class="weekday" v-for="(day, index) in weekdays" :key="index">
        {{ day }}
      </div>
    </div>
    <div class="days">
        <div class="day"
             v-for="(day, index) in paddedDaysInMonth"
             :key="index"
             @click="selectDate(day)"
             :class="{
               'selected': day.selected,
               'range': day.range,
               'disabled': day.disabled,
               'not-current-month': !day.isCurrentMonth,
               'range-start': day.isRangeStart,
               'range-end': day.isRangeEnd
             }"
        >
          <div class="day-text">
            {{ day.day }}
          </div>
          <div class="plan" v-if="day.plan.length">
            <template v-for="(plan, index) in day.plan">
              <div class="plan-item" :style="`background-color: ${plan.color}`" :key="index" />
            </template>
          </div>
        </div>
    </div>
  </div>
</template>

<script>
import {isSameDay} from "@/utils";

export default {
  props: {
    mode: {
      type: String,
      default: 'range' // 'single' or 'range'
    },
    plan: {
      type: Array,
      default: () => [] // Array of disabled dates
    },
    notSelectable: {
      type: Boolean,
      default: true
    },
    disabledDates: {
      type: Array,
      default: () => [] // Array of disabled dates
    },
    value: {
      type: Object,
      require: true,
      default: () => {
        if (this.mode === 'range') {
          return {
            startDate: null,
            endDate: null
          };
        } else {
          return {
            date: null
          }
        }
      }
    }
  },
  data() {
    return {
      currentDate: new Date(),
      startDate: null,
      endDate: null,
      weekdays: ['日', '一', '二', '三', '四', '五', '六']
    };
  },
  computed: {
    currentYear() {
      return this.currentDate.getFullYear();
    },
    currentMonth() {
      return this.currentDate.getMonth();
    },
    daysInMonth() {
      const year = this.currentYear;
      const month = this.currentMonth;
      const days = [];
      const date = new Date(year, month, 1);

      while (date.getMonth() === month) {
        days.push({
          date: new Date(date),
          day: date.getDate(),
          isCurrentMonth: true
        });
        date.setDate(date.getDate() + 1);
      }
      return days;
    },
    paddedDaysInMonth() {
      const days = this.daysInMonth;
      const firstDay = days[0].date.getDay();
      const lastDay = days[days.length - 1].date.getDay();
      const paddedDays = [];
      // Add days from the previous month
      if (firstDay > 0) {
        const prevMonthDate = new Date(this.currentYear, this.currentMonth, 0);
        const daysInPrevMonth = prevMonthDate.getDate();
        for (let i = firstDay - 1; i >= 0; i--) {
          const date = new Date(this.currentYear, this.currentMonth - 1, daysInPrevMonth - i);
          const isRangeStart = this.mode === 'range' && this.value.startDate && date.toDateString() === this.value.startDate.toDateString();
          const isRangeEnd = this.mode === 'range' && this.value.endDate && date.toDateString() === this.value.endDate.toDateString();
          paddedDays.push(this.createDayObject(date, false, isRangeStart ?? isRangeEnd, isRangeEnd ?? isRangeStart));
        }
      }

      // Add current month days
      days.forEach((day) => {
        const isRangeStart = this.mode === 'range' && this.value.startDate && day.date.toDateString() === this.value.startDate.toDateString();
        const isRangeEnd = this.mode === 'range' && this.value.endDate && day.date.toDateString() === this.value.endDate.toDateString();
        paddedDays.push(this.createDayObject(day.date, true, isRangeStart ?? isRangeEnd, isRangeEnd ?? isRangeStart));
      });

      // Add days from the next month
      if (lastDay < 6) {
        for (let i = 1; i <= 6 - lastDay; i++) {
          const date = new Date(this.currentYear, this.currentMonth + 1, i);
          const isRangeStart = this.mode === 'range' && this.value.startDate && date.toDateString() === this.value.startDate.toDateString();
          const isRangeEnd = this.mode === 'range' && this.value.endDate && date.toDateString() === this.value.endDate.toDateString();
          paddedDays.push(this.createDayObject(date, false, isRangeStart ?? isRangeEnd, isRangeEnd ?? isRangeStart));
        }
      }
      return paddedDays;
    }
  },
  methods: {
    createDayObject(date, isCurrentMonth, isRangeStart = false, isRangeEnd = false) {
      const plan = this.plan.filter((item) => isSameDay(date, item.date));
      return {
        date: date,
        day: date.getDate(),
        isCurrentMonth: isCurrentMonth,
        selected: this.isSelected(date),
        range: this.isInRange(date),
        disabled: this.isDisabled(date),
        isRangeStart: isRangeStart,
        isRangeEnd: isRangeEnd,
        plan: plan.slice(0, 3) // 只取3个
      };
    },
    prevMonth() {
      this.currentDate = new Date(this.currentYear, this.currentMonth - 1, 1);
    },
    nextMonth() {
      this.currentDate = new Date(this.currentYear, this.currentMonth + 1, 1);
    },
    selectDate(day) {
      if (this.notSelectable || !day.date || day.disabled) {
        return
      }

      const date = day.date
      if (this.mode === 'single') {
        this.$emit("input", {date: date})
      } else if (this.mode === 'range') {
        if (!this.value.startDate || (this.value.startDate && this.value.endDate)) {
          this.$emit("input", {startDate: date, endDate: null})
        } else if (!this.value.endDate) {
          if (date < this.value.startDate) {
            this.$emit("input", {startDate: date, endDate: this.value.startDate})
          } else {
            this.$emit("input", {startDate: this.value.startDate, endDate: date})
          }
        }
      }
      this.$nextTick().then(() => {
        this.$emit("selected", this.value)
      })
    },
    isSelected(date) {
      if (this.mode === 'single') {
        return this.value.date && this.value.date.toDateString() === date.toDateString();
      }
      return false;
    },
    isInRange(date) {
      if (this.mode === 'range' && this.value.startDate && this.value.endDate) {
        return date >= this.value.startDate && date <= this.value.endDate;
      } else if (this.mode === 'range' && this.value.startDate && !this.value.endDate) {
        return date.toDateString() === this.value.startDate.toDateString();
      }
      return false;
    },
    isDisabled(date) {
      return this.disabledDates.some(disabledDate =>
          new Date(disabledDate).toDateString() === date.toDateString()
      );
    }
  },
};
</script>

<style scoped>
.date-picker {
  overflow: hidden;
}

.header {
  display: flex;
  justify-content: space-between;
  padding: 0 11px;
  box-sizing: border-box;
  font-size: 18px;
  font-weight: 600;
  letter-spacing: 0;
  line-height: 33px;
  color: rgba(0, 0, 0, 1);
}

.weekdays {
  display: flex;
  margin: 14px 0;
}

.weekday {
  width: 14.28%;
  text-align: center;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.8);
  vertical-align: middle;
}

.days {
  display: flex;
  flex-wrap: wrap;
  background-color: #fff;
}

.day {
  position: relative;
  width: 14.28%;
  height: 36px;
  text-align: center;
  cursor: pointer;
  box-sizing: border-box;
  color: #000000;
  margin: 5px 0;
}
.day-text {
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0;
  line-height: 0;
  text-align: center;
  vertical-align: middle;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}
.plan {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
}
.plan-item {
  display: inline-block;
  width: 4px;
  height: 4px;
  border-radius: 50%;
  margin-right: 3px;
}
.plan-item:last-child {
  margin-right: 0;
}

.day.not-current-month {
  color: rgba(0, 0, 0, 0.4);
}

.day.selected {
  color: #ffffff
}
.day.selected:before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(10, 136, 255, 1);
}


.day.range {
  background-color: rgba(226, 241, 255, 1);
}

.day.disabled {
  background-color: #f0f0f0;
  color: #ccc;
  cursor: not-allowed;
}

.day.range-start {
  color: #fff;
  background: linear-gradient(to right, transparent 0%,transparent 50%, rgba(226, 241, 255, 1) 51%,rgba(226, 241, 255, 1) 100%);
}
.day.range-end {
  color: #fff;
  background: linear-gradient(to right, rgba(226, 241, 255, 1) 0%,rgba(226, 241, 255, 1) 50%, transparent 51%,transparent 100%);
}
.day.range-start.range-end {
  background: transparent;
}
.day.range-start:before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(10, 136, 255, 1);
}

.day.range-end:before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(10, 136, 255, 1);
}
</style>